import { createTheme, ThemeOptions } from '@mui/material/styles';

// ============================================================
// UNIRAC OFFICIAL BRAND DESIGN TOKENS
// Source: unirac.com CSS (verified 2026-04)
// ============================================================
const colors = {
  // Core brand
  uniracNavy:         '#003B5C',   // primary brand navy — nav, headings
  uniracNavyDark:     '#012A46',   // darker navy
  uniracNavyDeeper:   '#002F4A',   // deepest navy
  uniracOrange:       '#FE6900',   // primary CTA — buttons, links, actions
  uniracOrangeDark:   '#D45500',   // hover/press orange
  uniracOrangeLight:  '#FF8533',   // light orange tint

  // Blues
  uniracBlue:         '#0086BF',   // informational / interactive blue
  uniracSky:          '#9BCBEA',   // sky blue — highlights
  uniracSkyLight:     '#B9DBF0',   // lighter sky
  uniracSkyXLight:    '#E6F2FA',   // panel backgrounds
  uniracSkyXXLight:   '#F0F8FD',   // hover states, near-white

  // Neutrals
  uniracDark:         '#393939',   // body text
  uniracGrey900:      '#393939',
  uniracGrey800:      '#4D4D4D',
  uniracGrey700:      '#5A5A5A',
  uniracGrey600:      '#6C757D',
  uniracGrey500:      '#999999',
  uniracGrey400:      '#C5C5C5',
  uniracGrey300:      '#D1D3D4',   // dividers / borders
  uniracGrey200:      '#E9ECEF',
  uniracGrey100:      '#F6F6F6',   // page background
  white:              '#FFFFFF',

  // Status
  success:            '#003B5C',
  warning:            '#FEBB33',
  error:              '#D92B20',
  info:               '#0086BF',
};

const themeOptions: ThemeOptions = {
  palette: {
    primary: {
      main:          colors.uniracOrange,
      light:         colors.uniracOrangeLight,
      dark:          colors.uniracOrangeDark,
      contrastText:  colors.white,
    },
    secondary: {
      main:          colors.uniracNavy,
      light:         '#0D5E8F',
      dark:          colors.uniracNavyDark,
      contrastText:  colors.white,
    },
    info: {
      main:          colors.uniracBlue,
      light:         colors.uniracSkyLight,
      dark:          '#005F8C',
      contrastText:  colors.white,
    },
    success: {
      main:          colors.success,
      light:         '#68C934',
      dark:          '#357A0E',
      contrastText:  colors.white,
    },
    warning: {
      main:          colors.warning,
      light:         '#FED566',
      dark:          '#C8960C',
      contrastText:  colors.uniracDark,
    },
    error: {
      main:          colors.error,
      light:         '#E8554D',
      dark:          '#AE221A',
      contrastText:  colors.white,
    },
    grey: {
      50:   '#FAFAFA',
      100:  colors.uniracGrey100,
      200:  colors.uniracGrey200,
      300:  colors.uniracGrey300,
      400:  colors.uniracGrey400,
      500:  colors.uniracGrey500,
      600:  colors.uniracGrey600,
      700:  colors.uniracGrey700,
      800:  colors.uniracGrey800,
      900:  colors.uniracGrey900,
    },
    background: {
      default: colors.uniracGrey100,
      paper:   colors.white,
    },
    text: {
      primary:   colors.uniracDark,
      secondary: colors.uniracGrey600,
      disabled:  'rgba(0, 0, 0, 0.38)',
    },
    divider: colors.uniracGrey300,
    action: {
      hover:             colors.uniracSkyXXLight,
      selected:          colors.uniracSkyXLight,
      disabled:          'rgba(0, 0, 0, 0.38)',
      disabledBackground: colors.uniracGrey100,
      focus:             colors.uniracSkyXLight,
    },
  },
  typography: {
    fontFamily: '"PT Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    htmlFontSize: 18,
    fontSize: 16,
    h1: { fontFamily: '"Oswald", "PT Sans", sans-serif', fontWeight: 600, textTransform: 'uppercase' as const },
    h2: { fontFamily: '"Oswald", "PT Sans", sans-serif', fontWeight: 600, textTransform: 'uppercase' as const },
    h3: { fontFamily: '"Oswald", "PT Sans", sans-serif', fontWeight: 600, textTransform: 'uppercase' as const },
    h4: { fontFamily: '"Oswald", "PT Sans", sans-serif', fontWeight: 600, textTransform: 'uppercase' as const },
    h5: { fontFamily: '"Oswald", "PT Sans", sans-serif', fontWeight: 600, textTransform: 'uppercase' as const },
    h6: { fontFamily: '"Oswald", "PT Sans", sans-serif', fontWeight: 600, textTransform: 'uppercase' as const },
    subtitle1: { fontWeight: 600, color: colors.uniracNavy },
    subtitle2: { fontWeight: 600, color: colors.uniracGrey700 },
    body1:  { fontSize: '1rem', lineHeight: 1.6 },
    body2:  { fontSize: '0.9375rem', lineHeight: 1.6 },
    caption: { fontSize: '0.8125rem', color: colors.uniracGrey600, letterSpacing: '0.03em' },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase' as const,
      color: colors.uniracGrey600,
    },
    button: {
      textTransform: 'none' as const,
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 4,
  },
  spacing: 8,
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
    '0 3px 6px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.06)',
    '0 6px 12px rgba(0,0,0,0.08), 0 3px 6px rgba(0,0,0,0.06)',
    '0 8px 16px rgba(0,0,0,0.08), 0 4px 8px rgba(0,0,0,0.06)',
    '0 12px 24px rgba(0,0,0,0.08), 0 6px 12px rgba(0,0,0,0.06)',
    '0 16px 32px rgba(0,0,0,0.08), 0 8px 16px rgba(0,0,0,0.06)',
    '0 20px 40px rgba(0,0,0,0.10)',
    '0 24px 48px rgba(0,0,0,0.12)',
    '0 28px 56px rgba(0,0,0,0.12)',
    '0 32px 64px rgba(0,0,0,0.12)',
    '0 36px 72px rgba(0,0,0,0.14)',
    '0 40px 80px rgba(0,0,0,0.14)',
    '0 44px 88px rgba(0,0,0,0.14)',
    '0 48px 96px rgba(0,0,0,0.14)',
    '0 52px 104px rgba(0,0,0,0.16)',
    '0 56px 112px rgba(0,0,0,0.16)',
    '0 60px 120px rgba(0,0,0,0.16)',
    '0 64px 128px rgba(0,0,0,0.16)',
    '0 68px 136px rgba(0,0,0,0.16)',
    '0 72px 144px rgba(0,0,0,0.18)',
    '0 76px 152px rgba(0,0,0,0.18)',
    '0 80px 160px rgba(0,0,0,0.18)',
    '0 84px 168px rgba(0,0,0,0.18)',
    '0 88px 176px rgba(0,0,0,0.20)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.uniracGrey100,
          color: colors.uniracDark,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        a: {
          color: colors.uniracNavy,
          textDecoration: 'none',
          '&:hover': { color: colors.uniracOrange },
        },
        '::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '::-webkit-scrollbar-track': {
          background: colors.uniracGrey100,
        },
        '::-webkit-scrollbar-thumb': {
          borderRadius: '3px',
          backgroundColor: colors.uniracGrey400,
          '&:hover': { backgroundColor: colors.uniracGrey500 },
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '0.02em',
          padding: '7px 18px',
          lineHeight: 1.5,
          transition: 'all 150ms cubic-bezier(0.5, 0.1, 0.5, 0.9)',
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(254, 105, 0, 0.35)',
          },
          '&:active': { transform: 'translateY(0)' },
        },
        containedPrimary: {
          backgroundColor: colors.uniracOrange,
          '&:hover': { backgroundColor: colors.uniracOrangeDark },
        },
        containedSecondary: {
          backgroundColor: colors.uniracNavy,
          '&:hover': { backgroundColor: colors.uniracNavyDark },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': { borderWidth: '1.5px' },
        },
        outlinedPrimary: {
          borderColor: colors.uniracOrange,
          color: colors.uniracOrange,
          '&:hover': {
            backgroundColor: 'rgba(254, 105, 0, 0.06)',
            borderColor: colors.uniracOrangeDark,
          },
        },
        outlinedSecondary: {
          borderColor: colors.uniracNavy,
          color: colors.uniracNavy,
          '&:hover': {
            backgroundColor: colors.uniracSkyXXLight,
            borderColor: colors.uniracNavyDark,
          },
        },
        text: {
          '&:hover': { backgroundColor: colors.uniracSkyXXLight },
        },
        sizeLarge: { padding: '10px 24px', fontSize: '1rem' },
        sizeSmall: { padding: '4px 12px', fontSize: '0.8125rem' },
      },
    },

    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: colors.white,
            '& fieldset': { borderColor: colors.uniracGrey300 },
            '&:hover fieldset': { borderColor: colors.uniracGrey500 },
            '&.Mui-focused fieldset': {
              borderColor: colors.uniracBlue,
              borderWidth: '1.5px',
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.uniracGrey600,
            fontSize: '0.875rem',
            '&.Mui-focused': { color: colors.uniracBlue },
          },
          '& .MuiInputBase-input': {
            fontSize: '0.9rem',
            color: colors.uniracDark,
          },
        },
      },
    },

    MuiSelect: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.uniracGrey300 },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.uniracGrey500 },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.uniracBlue,
            borderWidth: '1.5px',
          },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.uniracGrey400,
          '&.Mui-checked': { color: colors.uniracBlue },
          '&:hover': { backgroundColor: colors.uniracSkyXXLight },
          padding: 6,
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: colors.uniracOrange,
            '& + .MuiSwitch-track': { backgroundColor: colors.uniracOrange },
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          fontWeight: 600,
          height: 24,
          borderRadius: 4,
        },
        colorPrimary: {
          backgroundColor: 'rgba(254, 105, 0, 0.12)',
          color: colors.uniracOrangeDark,
          border: `1px solid rgba(254, 105, 0, 0.3)`,
        },
        colorSecondary: {
          backgroundColor: colors.uniracSkyXLight,
          color: colors.uniracNavy,
          border: `1px solid ${colors.uniracSkyLight}`,
        },
        colorInfo: {
          backgroundColor: colors.uniracSkyXLight,
          color: colors.uniracBlue,
        },
        colorSuccess: {
          backgroundColor: '#EDF7E6',
          color: '#2B7A0B',
        },
        colorWarning: {
          backgroundColor: '#FFF5D6',
          color: '#8B6000',
        },
        colorError: {
          backgroundColor: '#FDECEA',
          color: colors.error,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.16)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"Oswald", sans-serif',
          fontWeight: 600,
          fontSize: '1.125rem',
          color: colors.uniracNavy,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          padding: '20px 24px 16px',
          borderBottom: `1px solid ${colors.uniracGrey200}`,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.uniracNavy,
          '& .MuiTableCell-head': {
            color: colors.white,
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            padding: '10px 16px',
            borderBottom: 'none',
          },
        },
      },
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:nth-of-type(even)': {
              backgroundColor: colors.uniracGrey100,
            },
            '&:hover': {
              backgroundColor: '#eeeeee',
            },
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.uniracGrey200}`,
          padding: '8px 16px',
          fontSize: '0.875rem',
        },
        head: {
          fontWeight: 600,
          color: colors.uniracNavy,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          letterSpacing: '0.02em',
          color: 'rgba(255,255,255,0.65)',
          minHeight: 48,
          padding: '0 20px',
          '&.Mui-selected': {
            color: colors.white,
          },
          '&:hover': {
            color: colors.white,
            backgroundColor: 'rgba(255,255,255,0.08)',
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: colors.uniracNavy,
          minHeight: 48,
        },
        indicator: {
          backgroundColor: colors.uniracOrange,
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.uniracNavy,
          color: colors.white,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '6px 10px',
          borderRadius: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
        arrow: {
          color: colors.uniracNavy,
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        root: { color: colors.uniracOrange },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: colors.uniracGrey200,
          borderRadius: 4,
          height: 4,
        },
        bar: {
          backgroundColor: colors.uniracOrange,
          borderRadius: 4,
        },
      },
    },

    MuiListItem: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: colors.uniracSkyXXLight },
          '&.Mui-selected': {
            backgroundColor: colors.uniracSkyXLight,
            '&:hover': { backgroundColor: colors.uniracSkyLight },
          },
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          '&:hover': { backgroundColor: colors.uniracSkyXXLight },
          '&.Mui-selected': {
            backgroundColor: colors.uniracSkyXLight,
            color: colors.uniracNavy,
            fontWeight: 600,
            '&:hover': { backgroundColor: colors.uniracSkyLight },
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          transition: 'all 150ms cubic-bezier(0.5, 0.1, 0.5, 0.9)',
          '&:hover': {
            backgroundColor: colors.uniracSkyXXLight,
            color: colors.uniracNavy,
          },
        },
        colorPrimary: {
          color: colors.uniracOrange,
          '&:hover': {
            backgroundColor: 'rgba(254, 105, 0, 0.08)',
            color: colors.uniracOrangeDark,
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 6 },
        standardSuccess: {
          backgroundColor: '#EDF7E6',
          color: '#2B7A0B',
          '& .MuiAlert-icon': { color: colors.success },
        },
        standardWarning: {
          backgroundColor: '#FFF5D6',
          color: '#8B6000',
          '& .MuiAlert-icon': { color: colors.warning },
        },
        standardError: {
          backgroundColor: '#FDECEA',
          color: '#B02017',
          '& .MuiAlert-icon': { color: colors.error },
        },
        standardInfo: {
          backgroundColor: colors.uniracSkyXLight,
          color: colors.uniracNavy,
          '& .MuiAlert-icon': { color: colors.uniracBlue },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${colors.uniracGrey200}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
        },
        elevation2: {
          boxShadow: '0 3px 6px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.06)',
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: colors.uniracOrange,
          color: colors.white,
        },
        colorSecondary: {
          backgroundColor: colors.uniracNavy,
          color: colors.white,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.uniracGrey200,
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${colors.uniracGrey200}`,
          borderRadius: 8,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.uniracNavy,
            color: colors.white,
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: colors.uniracGrey100,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: colors.uniracSkyXXLight,
          },
          '& .MuiDataGrid-cell:focus': {
            outline: `2px solid ${colors.uniracBlue}`,
          },
          '& .MuiDataGrid-columnSeparator': {
            color: 'rgba(255,255,255,0.2)',
          },
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          border: `1px solid ${colors.uniracGrey200}`,
          borderRadius: '6px !important',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 44,
          padding: '0 16px',
          '&.Mui-expanded': {
            backgroundColor: colors.uniracSkyXLight,
          },
          '& .MuiAccordionSummary-content': {
            fontWeight: 600,
            color: colors.uniracNavy,
            margin: '10px 0',
          },
        },
      },
    },

    MuiSlider: {
      styleOverrides: {
        root: {
          color: colors.uniracOrange,
        },
        thumb: {
          '&:hover, &.Mui-focusVisible': {
            boxShadow: `0 0 0 8px rgba(254, 105, 0, 0.16)`,
          },
        },
        track: {
          height: 4,
          borderRadius: 2,
        },
        rail: {
          height: 4,
          borderRadius: 2,
          backgroundColor: colors.uniracGrey300,
        },
      },
    },
  },
};

export const muiTheme = createTheme(themeOptions);

export { colors };
